import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';

import { getVariantWithMinimalPrice } from '../../../../utils/priceVariantsUtils';
import { formatPrice } from '../../../../utils/price';
import { getAltText, getImageUrl } from '../../../../utils/imgUtils';
import { useGetLayoutSize } from '../../../../components-shared/LayoutSizeProvider';
import {
  LABELS_LIMIT,
  ITEM_LAYOUT_TYPE,
  ITEM_IMAGE_PLACEMENT,
} from '../../constants';
import { MenuItem } from '../../../../types/menu';
import settingsParams from '../../settingsParams';
import type { ResolvedItemAvailability } from '../../../../utils/menuAvailability';

import {
  DISH_IMAGE_SIZE,
  DISH_CARD_IMAGE_WIDTH,
  DISH_CARD_IMAGE_HEIGHT,
} from './constants';

const getItemPrice = (item: MenuItem): string => {
  if (item.priceVariants?.variants?.length) {
    const minVariant = getVariantWithMinimalPrice(item.priceVariants.variants);

    if (minVariant?.priceInfo?.price) {
      return formatPrice(
        Number(minVariant.priceInfo.price),
        item.price.currency,
      );
    }
  }

  return formatPrice(item.price.amount, item.price.currency);
};

const isItemInStock = (item: MenuItem): boolean =>
  item.orderSettings?.inStock !== false;

type UseDishItemParams = {
  item: MenuItem;
  menuId: string;
  sectionId: string;
  availability?: ResolvedItemAvailability;
  onClick: (
    item: MenuItem,
    menuId: string,
    sectionId: string,
    availability?: ResolvedItemAvailability,
  ) => void;
};

export const useHooks = ({
  item,
  menuId,
  sectionId,
  availability,
  onClick,
}: UseDishItemParams) => {
  const { t } = useTranslation();
  const { layoutSize } = useGetLayoutSize();
  const settings = useSettings();

  const inStock = isItemInStock(item);
  const isAvailable = availability?.isAvailable ?? inStock;
  const visibleLabels = (item.labels ?? []).filter((l) => l.icon?.id);
  const shownLabels = visibleLabels.slice(0, LABELS_LIMIT);
  const extraLabelCount = visibleLabels.length - LABELS_LIMIT;

  const shouldShowImage =
    settings.get(settingsParams.showItemImage) && Boolean(item.image?.id);
  const shouldShowDescription =
    settings.get(settingsParams.showItemDescription) &&
    Boolean(item.description);
  const shouldShowLabels =
    settings.get(settingsParams.showItemLabels) && visibleLabels.length > 0;
  const shouldShowFeatured =
    settings.get(settingsParams.showItemFeatured) && Boolean(item.featured);
  const shouldShowLabelsRow = shouldShowLabels || shouldShowFeatured;

  const isCardLayout =
    settings.get(settingsParams.itemLayoutType) === ITEM_LAYOUT_TYPE.CARD;
  const isImageLeft =
    !isCardLayout &&
    settings.get(settingsParams.itemImagePlacement) ===
      ITEM_IMAGE_PLACEMENT.LEFT;

  const handleClick = () => {
    onClick(item, menuId, sectionId, availability);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const priceText = isAvailable
    ? getItemPrice(item)
    : t('menu_olo.itemUnavailable.itemPrice', { price: getItemPrice(item) });

  const imageUrl = item.image
    ? getImageUrl(
        item.image,
        isCardLayout ? DISH_CARD_IMAGE_WIDTH : DISH_IMAGE_SIZE,
        isCardLayout ? DISH_CARD_IMAGE_HEIGHT : DISH_IMAGE_SIZE,
      )
    : '';
  const imageAlt = getAltText({ itemName: item.name });
  const featuredLabel = t('menu_olo.item.featured-icon-alt-text');

  return {
    layoutSize,
    isAvailable,
    isCardLayout,
    isImageLeft,
    shownLabels,
    extraLabelCount,
    shouldShowImage,
    shouldShowDescription,
    shouldShowLabels,
    shouldShowFeatured,
    shouldShowLabelsRow,
    handleClick,
    handleKeyDown,
    priceText,
    imageUrl,
    imageAlt,
    featuredLabel,
  };
};
